import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { getCouponsApi, createCouponApi, updateCouponApi, deleteCouponApi, toggleCouponApi } from '../../api/couponApi';
import AdminModal, { ConfirmModal } from './AdminModal';

const EMPTY = { code:'', type:'percentage', discount_value:'', min_order_amount:'0', max_discount:'', usage_limit:'', expires_at:'' };

const randCode = () => 'BHADRA' + Math.random().toString(36).slice(2,6).toUpperCase();

export default function CouponsTab() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]     = useState(null);
  const [editing, setEditing] = useState(EMPTY);
  const [delId, setDelId]     = useState(null);
  const [saving, setSaving]   = useState(false);

  const load = () => {
    setLoading(true);
    getCouponsApi().then((r) => setCoupons(r.data)).catch(() => toast.error('Failed to load coupons')).finally(() => setLoading(false));
  };

  useEffect(load, []);

  const openAdd  = () => { setEditing({ ...EMPTY, code: randCode() }); setModal('add'); };
  const openEdit = (c) => {
    setEditing({ code: c.code, type: c.type, discount_value: c.discount_value, min_order_amount: c.min_order_amount, max_discount: c.max_discount || '', usage_limit: c.usage_limit || '', expires_at: c.expires_at ? c.expires_at.slice(0,10) : '', is_active: c.is_active, _id: c.id });
    setModal('edit');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...editing, discount_value: parseFloat(editing.discount_value), min_order_amount: parseFloat(editing.min_order_amount) || 0, max_discount: editing.max_discount ? parseFloat(editing.max_discount) : null, usage_limit: editing.usage_limit ? parseInt(editing.usage_limit) : null, expires_at: editing.expires_at || null };
      if (modal === 'add') { await createCouponApi(payload); toast.success('Coupon created!'); }
      else { await updateCouponApi(editing._id, payload); toast.success('Coupon updated!'); }
      setModal(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try { await deleteCouponApi(delId); toast.success('Coupon deleted'); setDelId(null); load(); }
    catch { toast.error('Delete failed'); }
  };

  const handleToggle = async (id) => {
    try { const r = await toggleCouponApi(id); setCoupons((prev) => prev.map((c) => c.id === id ? r.data : c)); toast.success('Toggled'); }
    catch { toast.error('Failed'); }
  };

  const copyCode = (code) => { navigator.clipboard.writeText(code); toast.success(`Copied: ${code}`); };

  return (
    <div>
      <div style={S.hdr}>
        <div>
          <h1 style={S.title}>Coupons</h1>
          <p style={S.sub}>{coupons.length} coupons total</p>
        </div>
        <button style={S.addBtn} onClick={openAdd}>+ Create Coupon</button>
      </div>

      {/* Summary row */}
      <div style={S.statRow}>
        {[
          { label: 'Total Coupons', val: coupons.length },
          { label: 'Active', val: coupons.filter((c) => c.is_active).length },
          { label: 'Times Used', val: coupons.reduce((s, c) => s + (c.used_count || 0), 0) },
          { label: 'Expired', val: coupons.filter((c) => c.expires_at && new Date(c.expires_at) < new Date()).length },
        ].map((s) => (
          <div key={s.label} style={S.statCard}>
            <p style={S.statLabel}>{s.label}</p>
            <p style={S.statVal}>{s.val}</p>
          </div>
        ))}
      </div>

      {/* Table */}
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead>
            <tr>{['Code','Type','Discount','Min Order','Usage','Expires','Status','Actions'].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {loading
              ? <tr><td colSpan={8} style={{ textAlign:'center', padding:'48px', color:'#aaa' }}>Loading...</td></tr>
              : coupons.length === 0
                ? <tr><td colSpan={8} style={{ textAlign:'center', padding:'48px', color:'#aaa' }}>No coupons yet. Create your first one!</td></tr>
                : coupons.map((c) => {
                  const expired = c.expires_at && new Date(c.expires_at) < new Date();
                  const usagePct = c.usage_limit ? Math.min(100, Math.round((c.used_count / c.usage_limit) * 100)) : null;
                  return (
                    <tr key={c.id} style={S.tr}>
                      <td style={S.td}>
                        <div style={{ display:'flex', alignItems:'center', gap:'6px' }}>
                          <span style={S.codeTag}>{c.code}</span>
                          <button style={S.copyBtn} onClick={() => copyCode(c.code)} title="Copy">📋</button>
                        </div>
                      </td>
                      <td style={S.td}>
                        <span style={{ ...S.typeBadge, background: c.type === 'percentage' ? '#EEF2FF' : '#FEF3C7', color: c.type === 'percentage' ? '#4338CA' : '#D97706' }}>
                          {c.type === 'percentage' ? '% Off' : 'Flat ₹'}
                        </span>
                      </td>
                      <td style={S.td}>
                        <strong style={{ color: 'var(--red)' }}>
                          {c.type === 'percentage' ? `${c.discount_value}%` : `₹${c.discount_value}`}
                        </strong>
                        {c.max_discount && <p style={{ fontSize:'11px', color:'#9CA3AF', margin:0 }}>Max ₹{c.max_discount}</p>}
                      </td>
                      <td style={S.td}>₹{parseFloat(c.min_order_amount).toLocaleString('en-IN')}</td>
                      <td style={S.td}>
                        <div>
                          <span style={{ fontSize:'12px', fontWeight:600 }}>
                            {c.used_count}{c.usage_limit ? ` / ${c.usage_limit}` : ''}
                          </span>
                          {usagePct !== null && (
                            <div style={{ height:'4px', background:'#F3F4F6', borderRadius:'99px', marginTop:'4px', width:'80px' }}>
                              <div style={{ height:'100%', width:`${usagePct}%`, background: usagePct >= 90 ? '#EF4444' : usagePct >= 60 ? '#F59E0B' : '#16A34A', borderRadius:'99px' }} />
                            </div>
                          )}
                        </div>
                      </td>
                      <td style={S.td}>
                        {c.expires_at
                          ? <span style={{ fontSize:'12px', color: expired ? '#EF4444' : '#374151', fontWeight: expired ? 700 : 400 }}>
                              {expired ? '⛔ ' : ''}{new Date(c.expires_at).toLocaleDateString('en-IN')}
                            </span>
                          : <span style={{ color:'#9CA3AF', fontSize:'12px' }}>No expiry</span>
                        }
                      </td>
                      <td style={S.td}>
                        <span style={{ ...S.pill, background: c.is_active && !expired ? '#DCFCE7' : '#F3F4F6', color: c.is_active && !expired ? '#16A34A' : '#9CA3AF' }}>
                          {c.is_active && !expired ? 'Active' : expired ? 'Expired' : 'Inactive'}
                        </span>
                      </td>
                      <td style={S.td}>
                        <div style={S.actionRow}>
                          <button style={S.iconBtn} onClick={() => openEdit(c)} title="Edit">✏️</button>
                          <button style={{ ...S.iconBtn, background: c.is_active ? '#FEF3C7' : '#DCFCE7' }} onClick={() => handleToggle(c.id)} title={c.is_active ? 'Deactivate' : 'Activate'}>
                            {c.is_active ? '⏸️' : '▶️'}
                          </button>
                          <button style={{ ...S.iconBtn, color:'#EF4444' }} onClick={() => setDelId(c.id)} title="Delete">🗑️</button>
                        </div>
                      </td>
                    </tr>
                  );
                })
            }
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {modal && (
        <AdminModal title={modal === 'add' ? 'CREATE COUPON' : 'EDIT COUPON'} onClose={() => setModal(null)} width="500px">
          <form onSubmit={handleSave}>
            <div style={S.formRow}>
              <div style={{ flex: 1 }}>
                <label style={S.lbl}>Coupon Code *</label>
                <input style={{ ...S.inp, textTransform:'uppercase', letterSpacing:'2px', fontWeight:700 }} value={editing.code}
                  onChange={(e) => setEditing({ ...editing, code: e.target.value.toUpperCase() })} required />
              </div>
              <button type="button" style={S.genBtn} onClick={() => setEditing({ ...editing, code: randCode() })}>🔀 Generate</button>
            </div>

            <div style={S.formRow}>
              <div style={{ flex: 1 }}>
                <label style={S.lbl}>Discount Type *</label>
                <select style={S.inp} value={editing.type} onChange={(e) => setEditing({ ...editing, type: e.target.value })} required>
                  <option value="percentage">Percentage (%)</option>
                  <option value="flat">Flat Amount (₹)</option>
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label style={S.lbl}>Discount Value *</label>
                <input style={S.inp} type="number" step="0.01" min="0.01" placeholder={editing.type === 'percentage' ? 'e.g. 15' : 'e.g. 100'}
                  value={editing.discount_value} onChange={(e) => setEditing({ ...editing, discount_value: e.target.value })} required />
              </div>
            </div>

            <div style={S.formRow}>
              <div style={{ flex: 1 }}>
                <label style={S.lbl}>Min Order Amount (₹)</label>
                <input style={S.inp} type="number" min="0" value={editing.min_order_amount} onChange={(e) => setEditing({ ...editing, min_order_amount: e.target.value })} />
              </div>
              {editing.type === 'percentage' && (
                <div style={{ flex: 1 }}>
                  <label style={S.lbl}>Max Discount (₹)</label>
                  <input style={S.inp} type="number" min="0" placeholder="No limit" value={editing.max_discount} onChange={(e) => setEditing({ ...editing, max_discount: e.target.value })} />
                </div>
              )}
            </div>

            <div style={S.formRow}>
              <div style={{ flex: 1 }}>
                <label style={S.lbl}>Usage Limit</label>
                <input style={S.inp} type="number" min="1" placeholder="Unlimited" value={editing.usage_limit} onChange={(e) => setEditing({ ...editing, usage_limit: e.target.value })} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={S.lbl}>Expires At</label>
                <input style={S.inp} type="date" value={editing.expires_at} onChange={(e) => setEditing({ ...editing, expires_at: e.target.value })} />
              </div>
            </div>

            <div style={S.previewBox}>
              <p style={{ fontSize:'11px', color:'#6B7280', marginBottom:'4px', fontWeight:700, letterSpacing:'1px', textTransform:'uppercase' }}>Preview</p>
              <p style={{ fontFamily:'var(--font-display)', fontSize:'18px', letterSpacing:'3px', color:'var(--red)', margin:0 }}>{editing.code || 'CODE'}</p>
              <p style={{ fontSize:'12px', color:'#555', margin:'4px 0 0' }}>
                {editing.type === 'percentage' ? `${editing.discount_value || '?'}% off` : `₹${editing.discount_value || '?'} off`}
                {editing.min_order_amount > 0 ? ` on orders above ₹${editing.min_order_amount}` : ''}
              </p>
            </div>

            <div style={S.formActions}>
              <button type="button" style={S.cancelBtn} onClick={() => setModal(null)}>Cancel</button>
              <button type="submit" style={S.saveBtn} disabled={saving}>{saving ? 'Saving...' : modal === 'add' ? 'Create Coupon' : 'Save Changes'}</button>
            </div>
          </form>
        </AdminModal>
      )}

      {delId && <ConfirmModal message="Delete this coupon? This cannot be undone." onConfirm={handleDelete} onCancel={() => setDelId(null)} />}
    </div>
  );
}

const S = {
  hdr:        { display:'flex', justifyContent:'space-between', alignItems:'flex-end', marginBottom:'16px' },
  title:      { fontFamily:'var(--font-display)', fontSize:'24px', letterSpacing:'1.5px', color:'#111', margin:0 },
  sub:        { fontSize:'13px', color:'#9CA3AF', marginTop:'2px' },
  addBtn:     { background:'#111', color:'#fff', border:'none', padding:'10px 20px', fontFamily:'var(--font-body)', fontSize:'12px', fontWeight:700, letterSpacing:'1px', cursor:'pointer' },
  statRow:    { display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'14px', marginBottom:'16px' },
  statCard:   { background:'#fff', border:'1px solid #E5E5E5', padding:'16px', boxShadow:'0 1px 3px rgba(0,0,0,.04)' },
  statLabel:  { fontSize:'10px', fontWeight:700, letterSpacing:'1.5px', textTransform:'uppercase', color:'#9CA3AF', marginBottom:'4px' },
  statVal:    { fontFamily:'var(--font-display)', fontSize:'22px', letterSpacing:'1px', color:'#111', margin:0 },
  tableWrap:  { background:'#fff', border:'1px solid #E5E5E5', overflow:'auto' },
  table:      { width:'100%', borderCollapse:'collapse', minWidth:'800px' },
  th:         { padding:'10px 14px', fontSize:'10px', fontWeight:700, letterSpacing:'1.5px', textTransform:'uppercase', color:'#9CA3AF', borderBottom:'1px solid #E5E5E5', textAlign:'left', background:'#FAFAFA', whiteSpace:'nowrap' },
  tr:         { borderBottom:'1px solid #F9FAFB', transition:'background .1s' },
  td:         { padding:'12px 14px', fontSize:'13px', verticalAlign:'middle' },
  codeTag:    { fontFamily:'monospace', fontWeight:700, fontSize:'13px', letterSpacing:'1.5px', background:'#F3F4F6', padding:'3px 8px', color:'#111' },
  copyBtn:    { background:'none', border:'none', cursor:'pointer', fontSize:'14px', padding:'2px 4px', opacity:.7 },
  typeBadge:  { display:'inline-block', fontSize:'10px', fontWeight:700, padding:'3px 9px' },
  pill:       { display:'inline-block', fontSize:'10px', fontWeight:700, padding:'3px 9px', borderRadius:'99px' },
  actionRow:  { display:'flex', gap:'4px' },
  iconBtn:    { background:'#F9FAFB', border:'1px solid #E5E5E5', padding:'5px 8px', fontSize:'13px', cursor:'pointer' },
  formRow:    { display:'flex', gap:'12px', marginBottom:'0', alignItems:'flex-end' },
  lbl:        { display:'block', fontSize:'10px', fontWeight:700, letterSpacing:'1px', textTransform:'uppercase', color:'#6B7280', marginBottom:'5px', marginTop:'14px' },
  inp:        { width:'100%', border:'1.5px solid #E5E5E5', background:'#FAFAFA', padding:'9px 11px', fontFamily:'var(--font-body)', fontSize:'13px', outline:'none', boxSizing:'border-box' },
  genBtn:     { background:'#F9FAFB', border:'1.5px solid #E5E5E5', padding:'0 14px', fontFamily:'var(--font-body)', fontSize:'11px', fontWeight:700, cursor:'pointer', marginTop:'22px', whiteSpace:'nowrap', height:'38px' },
  previewBox: { background:'#FAFAFA', border:'2px dashed #E5E5E5', padding:'16px', marginTop:'16px', textAlign:'center' },
  formActions:{ display:'flex', justifyContent:'flex-end', gap:'10px', marginTop:'20px', paddingTop:'16px', borderTop:'1px solid #F3F4F6' },
  cancelBtn:  { padding:'9px 20px', background:'#fff', border:'1.5px solid #E5E5E5', fontFamily:'var(--font-body)', fontSize:'12px', fontWeight:600, cursor:'pointer', color:'#374151' },
  saveBtn:    { padding:'9px 24px', background:'#111', color:'#fff', border:'none', fontFamily:'var(--font-body)', fontSize:'12px', fontWeight:700, letterSpacing:'1px', cursor:'pointer' },
};
